import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import type { ChangeAgendaQueuedSendSummaryItem } from '../../../types/changes';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  items: ChangeAgendaQueuedSendSummaryItem[];
  onChanged: () => void;
  onInfo?: (message: string) => void;
}

const AgendaQueuedDispatchList: React.FC<Props> = ({ items, onChanged, onInfo }) => {
  const { showConfirm } = useModal();
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!items.length) return null;

  const handleCancel = async (item: ChangeAgendaQueuedSendSummaryItem) => {
    const ok = await showConfirm({
      title: 'Cancelar disparo agendado',
      message: `Cancelar o e-mail «${item.agenda_kind_label}» agendado para ${formatWhen(item.send_at)}?`,
      details:
        'O envio não será feito nessa data. Para outro horário, cancele e use «Enviar e-mail» de novo com a data desejada.',
      variant: 'danger',
      confirmLabel: 'Cancelar agendamento',
      cancelLabel: 'Manter na fila',
    });
    if (!ok) return;

    setCancellingId(item.queued_id);
    setError(null);
    try {
      await changesApi.cancelQueuedAgendaSend(item.queued_id);
      onInfo?.(`Agendamento #${item.queued_id} cancelado.`);
      onChanged();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível cancelar o agendamento.'));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="border rounded p-2 mb-2 bg-white">
      <div className="small fw-semibold mb-1">Disparos agendados na fila</div>
      <div className="small text-muted mb-2">
        Cancele aqui se não quiser mais enviar nesse horário. Para reagendar, cancele e envie de novo.
      </div>
      {error ? <div className="small text-danger mb-2">{error}</div> : null}
      <ul className="list-unstyled mb-0 small">
        {items.map((item) => (
          <li
            key={item.queued_id}
            className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-1 border-top"
          >
            <span>
              {item.agenda_kind_label} · {formatWhen(item.send_at)} · fila #{item.queued_id}
            </span>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={cancellingId === item.queued_id}
              onClick={() => void handleCancel(item)}
            >
              {cancellingId === item.queued_id ? 'Cancelando…' : 'Cancelar agendamento'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AgendaQueuedDispatchList;
